import React, { useState } from "react";
import { format } from "date-fns";
import { Icon } from "@iconify/react";
import { completionStatusLabel } from "../../utils/completionStatus";
import { isDaniyalUserId } from "../../utils/isDaniyalUser";
import DeleteModalForLogs from "../DeleteModalForLogs";
import TablePagination from "./TablePagination";
import TruncatedTooltipCell from "./TruncatedTooltipCell";

/** Compact display for applied hours + minutes (e.g. `1h 11m`, `25m`). */
function formatAppliedDuration(hours: unknown, minutes: unknown): string {
  const h = Number(hours);
  const m = Number(minutes);
  const safeH = Number.isFinite(h) ? Math.max(0, Math.floor(h)) : 0;
  const safeM = Number.isFinite(m) ? Math.max(0, Math.floor(m)) : 0;
  if (safeH === 0 && safeM === 0) return "0m";
  if (safeH === 0) return `${safeM}m`;
  if (safeM === 0) return `${safeH}h`;
  return `${safeH}h ${safeM}m`;
}

export type LoggedHoursProps = {
  user: { user_id?: string } | null | undefined;
  DANIYAL: string;
  tasks1: any;
  tasks2: any;
  logSearchQuery: string;
  handleLogSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setSelectedLog: React.Dispatch<React.SetStateAction<any>>;
  logId: any;
  setLogId: React.Dispatch<React.SetStateAction<any>>;
  setTasks: React.Dispatch<React.SetStateAction<any>>;
  setTasks1: React.Dispatch<React.SetStateAction<any>>;
  setTasks2: React.Dispatch<React.SetStateAction<any>>;
  currentPage1: number;
  totalPages1: number;
  goToPage1: (page: number) => void;
  goToPreviousPage1: () => void;
  goToNextPage1: () => void;
  subtaskLogsCurrentPage: number;
  totalPagesSubtaskLogs: number;
  goToPageSubTaskLogs: (page: number) => void;
  goToPreviousSubTaskLogs: () => void;
  goToNextPageSubTaskLogs: () => void;
};

const LoggedHours = ({
  user,
  DANIYAL,
  tasks1,
  tasks2,
  logSearchQuery,
  handleLogSearchChange,
  setSelectedLog,
  logId,
  setLogId,
  setTasks,
  setTasks1,
  setTasks2,
  currentPage1,
  totalPages1,
  goToPage1,
  goToPreviousPage1,
  goToNextPage1,
  subtaskLogsCurrentPage,
  totalPagesSubtaskLogs,
  goToPageSubTaskLogs,
  goToPreviousSubTaskLogs,
  goToNextPageSubTaskLogs,
}: LoggedHoursProps) => {
  const jobLogs = tasks1?.logs ?? [];
  const subtaskLogs = tasks2?.logs ?? [];
  const [viewLog, setViewLog] = useState<any | null>(null);
  const isDaniyalUser = isDaniyalUserId(user?.user_id, DANIYAL);

  const LOG_ICONS = {
    view: "mdi:eye-outline",
    edit: "mdi:pencil-outline",
    delete: "mdi:delete-outline",
  } as const;

  return (
    <>
      {!isDaniyalUser && (
        <>
          <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2">
            <div className="heading">
              <h2>Logged Hours</h2>
            </div>
            <div className="button"></div>
          </div>
          <div className="search-bar employee-logged-hours-search">
            <input
              type="text"
              placeholder="Search"
              className="form-control mb-3"
              value={logSearchQuery}
              onChange={handleLogSearchChange}
            />
          </div>

          <div className="table-responsive employee-logged-hours-table-responsive">
            <table className="table caption-top employee-logged-hours-table">
              <thead className="table-light">
                <tr>
                  <th scope="col" className="text-start">
                    Client / Group
                  </th>
                  <th scope="col" className="text-start loghours-id-header">
                    Job ID
                  </th>
                  <th scope="col" className="text-end text-nowrap loghours-col-date">
                    Date
                  </th>
                  <th
                    scope="col"
                    className="text-end text-nowrap loghours-col-duration"
                  >
                    App. time
                  </th>
                  <th scope="col" className="text-center loghours-col-status">
                    Status
                  </th>
                  <th scope="col" className="text-end text-nowrap loghours-actions-header">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {jobLogs.length > 0 ? (
                  jobLogs.map((task1: any, index: number) => (
                    <tr key={task1.logId ?? index}>
                      <td className="align-middle text-start loghours-client-cell">
                        <TruncatedTooltipCell text={task1?.clientGroup} />
                      </td>
                      <td
                        className="text-start align-middle loghours-id-cell"
                        title={String(task1?.projectId ?? "")}
                      >
                        <span className="loghours-id-value">
                          {String(task1?.projectId ?? "—")}
                        </span>
                      </td>
                      <td className="text-end align-middle text-nowrap loghours-col-date">
                        {format(task1.date, "dd-MM-yyyy")}
                      </td>
                      <td className="text-end align-middle text-nowrap loghours-col-duration">
                        {formatAppliedDuration(
                          task1?.appliedHrs,
                          task1?.appliedMins,
                        )}
                      </td>
                      <td className="text-center align-middle text-nowrap loghours-col-status">
                        {(() => {
                          const label = completionStatusLabel(
                            task1?.status,
                            task1?.statusBoolean,
                          );
                          if (label === "—")
                            return <span className="text-muted">—</span>;
                          const done = label === "Completed";
                          return (
                            <span
                              className={`badge rounded-pill ${
                                done
                                  ? "text-bg-success"
                                  : "text-bg-warning text-dark"
                              }`}
                            >
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="loghours-actions-cell text-end align-middle">
                        <div className="loghours-actions-toolbar" role="group" aria-label="Log actions">
                          <button
                            type="button"
                            className="loghours-action-btn"
                            title="View"
                            aria-label="View log details"
                            onClick={() => setViewLog(task1)}
                          >
                            <Icon icon={LOG_ICONS.view} width={24} height={24} aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="loghours-action-btn"
                            title="Edit"
                            aria-label="Edit log"
                            data-bs-toggle="modal"
                            data-bs-target="#editLogHoursModal"
                            onClick={() => {
                              setSelectedLog({
                                ...task1,
                                timeHrsApplied: task1.appliedHrs,
                                timeMinsApplied: task1.appliedMins,
                              });
                            }}
                          >
                            <Icon icon={LOG_ICONS.edit} width={24} height={24} aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="loghours-action-btn loghours-action-btn--danger"
                            title="Delete"
                            aria-label="Delete log"
                            data-bs-toggle="modal"
                            data-bs-target="#exampleModal5"
                            onClick={() => {
                              setLogId(task1.logId);
                            }}
                          >
                            <Icon icon={LOG_ICONS.delete} width={24} height={24} aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center">
                      No logged hours found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <DeleteModalForLogs
            task={null}
            logId={logId}
            setLogId={setLogId}
            setTasks={setTasks}
            setTasks1={setTasks1}
          />

          <TablePagination
            currentPage={currentPage1}
            totalPages={totalPages1}
            onPageChange={goToPage1}
            onPrevious={goToPreviousPage1}
            onNext={goToNextPage1}
            className="employee-table-pagination"
            ariaLabel="Job logged hours pagination"
          />
        </>
      )}

      {isDaniyalUser && (
        <>
          <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2">
            <div className="heading">
              <h2>Logged Hours</h2>
            </div>
            <div className="button"></div>
          </div>

          <div className="table-responsive employee-logged-hours-table-responsive">
            <table className="table caption-top employee-logged-hours-table employee-logged-hours-subtask-table">
              <thead className="table-light">
                <tr>
                  <th scope="col" className="text-start">
                    SubJob name
                  </th>
                  <th scope="col" className="text-end text-nowrap loghours-col-date">
                    Date
                  </th>
                  <th
                    scope="col"
                    className="text-end text-nowrap loghours-col-duration"
                  >
                    App. time
                  </th>
                  <th scope="col" className="text-center loghours-col-status">
                    Status
                  </th>
                  <th scope="col" className="text-end text-nowrap loghours-actions-header">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {subtaskLogs.length > 0 ? (
                  subtaskLogs.map((task2: any, index: number) => (
                    <tr key={task2.logId ?? index}>
                      <td className="align-middle text-start loghours-name-cell">
                        <TruncatedTooltipCell text={task2?.subtaskName} />
                      </td>
                      <td className="text-end align-middle text-nowrap loghours-col-date">
                        {format(task2.date, "dd-MM-yyyy")}
                      </td>
                      <td className="text-end align-middle text-nowrap loghours-col-duration">
                        {formatAppliedDuration(
                          task2?.timeHrsApplied,
                          task2?.timeMinsApplied,
                        )}
                      </td>
                      <td className="text-center align-middle text-nowrap loghours-col-status">
                        {(() => {
                          const label = completionStatusLabel(
                            task2?.status,
                            task2?.statusBoolean,
                          );
                          if (label === "—")
                            return <span className="text-muted">—</span>;
                          const done = label === "Completed";
                          return (
                            <span
                              className={`badge rounded-pill ${
                                done
                                  ? "text-bg-success"
                                  : "text-bg-warning text-dark"
                              }`}
                            >
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="loghours-actions-cell text-end align-middle">
                        <div className="loghours-actions-toolbar" role="group" aria-label="Subtask log actions">
                          <button
                            type="button"
                            className="loghours-action-btn"
                            title="View"
                            aria-label="View log details"
                            onClick={() => setViewLog(task2)}
                          >
                            <Icon icon={LOG_ICONS.view} width={24} height={24} aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="loghours-action-btn loghours-action-btn--danger"
                            title="Delete"
                            aria-label="Delete log"
                            data-bs-toggle="modal"
                            data-bs-target="#exampleModal5"
                            onClick={() => {
                              setLogId(task2.logId);
                            }}
                          >
                            <Icon icon={LOG_ICONS.delete} width={24} height={24} aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center">
                      No logged hours found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <DeleteModalForLogs
            isSubtaskLog
            task={null}
            logId={logId}
            setLogId={setLogId}
            setTasks={setTasks}
            setTasks2={setTasks2}
          />

          <TablePagination
            currentPage={subtaskLogsCurrentPage}
            totalPages={totalPagesSubtaskLogs}
            onPageChange={goToPageSubTaskLogs}
            onPrevious={goToPreviousSubTaskLogs}
            onNext={goToNextPageSubTaskLogs}
            className="employee-table-pagination"
            ariaLabel="Subtask logged hours pagination"
          />
        </>
      )}

      {viewLog && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1080 }}
            aria-hidden
            onClick={() => setViewLog(null)}
          />
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="employeeLogDetailTitle"
            style={{ zIndex: 1085 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
              <div className="modal-content border-0 shadow employee-log-detail-card">
                <div className="employee-log-detail-header text-white p-4 pb-3 position-relative">
                  <h2 className="h5 mb-1 fw-semibold" id="employeeLogDetailTitle">
                    Logged Hours Detail
                  </h2>
                  <p className="mb-0 opacity-75 small">
                    Log #{String(viewLog?.logId ?? "—")}
                  </p>
                  <button
                    type="button"
                    className="btn-close btn-close-white position-absolute top-0 end-0 m-3"
                    aria-label="Close"
                    onClick={() => setViewLog(null)}
                  />
                </div>
                <div className="modal-body p-4">
                  <div className="row g-3">
                    <div className="col-sm-6">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Client / Group</div>
                        <div className="employee-log-detail-value">
                          {String(viewLog?.clientGroup ?? "—")}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">
                          Job / SubJob
                        </div>
                        <div className="employee-log-detail-value">
                          {String(
                            viewLog?.projectId ??
                              viewLog?.subtaskName ??
                              viewLog?.jobName ??
                              "—",
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Date</div>
                        <div className="employee-log-detail-value">
                          {viewLog?.date ? format(viewLog.date, "dd-MM-yyyy") : "—"}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-3 col-6">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Hours</div>
                        <div className="employee-log-detail-value">
                          {String(viewLog?.appliedHrs ?? viewLog?.timeHrsApplied ?? "0")}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-3 col-6">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Minutes</div>
                        <div className="employee-log-detail-value">
                          {String(
                            viewLog?.appliedMins ?? viewLog?.timeMinsApplied ?? "0",
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Status</div>
                        <div className="employee-log-detail-value">
                          {completionStatusLabel(
                            viewLog?.status,
                            viewLog?.statusBoolean,
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Notes</div>
                        <div className="employee-log-detail-value employee-log-detail-notes">
                          {String(viewLog?.notes ?? "—")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setViewLog(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default LoggedHours;
